import {
  Children,
  ElementType,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from 'react';

export interface ScrollspyProps {
  /** Element IDs (without leading `#`) to track in document order. */
  items: string[];
  /** Children rendered inside the wrapper, one per item in `items`. The active
   *  child receives `currentClassName` appended to its existing `className`. */
  children?: ReactNode;
  /** Class on the wrapper element. */
  className?: string;
  /** Class added to the child whose corresponding section is currently in view. */
  currentClassName?: string;
  /** Wrapper element (default `'ul'`). */
  componentTag?: ElementType;
  /** Pixel offset from the top of the viewport when computing "in view". */
  offset?: number;
}

interface ChildProps {
  className?: string;
  [key: string]: unknown;
}

/**
 * Drop-in replacement for `react-scrollspy` (which is unmaintained and trips
 * `UNSAFE_componentWillReceiveProps` warnings). Tracks which section is
 * visible via `IntersectionObserver` and decorates the matching child with
 * `currentClassName`.
 */
export default function Scrollspy({
  items,
  children,
  className,
  currentClassName = 'is-current',
  componentTag: Tag = 'ul',
  offset = 0,
}: ScrollspyProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const elements = items
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0 && visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: `${-offset}px 0px 0px 0px`,
        threshold: 0.1,
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items, offset]);

  return (
    <Tag className={className}>
      {Children.map(children, (child, index) => {
        const itemId = items[index];
        if (!isValidElement(child)) return child;
        const isActive = itemId !== undefined && itemId === activeId;
        if (!isActive) return child;
        const childProps = child.props as ChildProps;
        const merged = `${childProps.className ?? ''} ${currentClassName}`.trim();
        return cloneElement(child as ReactElement<ChildProps>, { className: merged });
      })}
    </Tag>
  );
}
